import React, { useEffect, useRef, useState, useMemo } from 'react';
import { StatusBar } from 'expo-status-bar';
import { StyleSheet, Text, View, Platform, TextInput, TouchableOpacity, Animated, Easing, Image } from 'react-native';
import { BlurView } from 'expo-blur';
import { LinearGradient } from 'expo-linear-gradient';
import { saveEmailCapture } from './firebaseConfig';
import { useNavigation } from './context/NavigationContext';

// Floating particle component
const Particle = ({ delay, duration, startX, startY, size, color }) => {
  const translateY = useRef(new Animated.Value(0)).current;
  const translateX = useRef(new Animated.Value(0)).current;
  const opacity = useRef(new Animated.Value(0)).current;
  const scale = useRef(new Animated.Value(0.5)).current;

  useEffect(() => {
    const animate = () => {
      // Reset
      translateY.setValue(0);
      translateX.setValue(0);
      opacity.setValue(0);
      scale.setValue(0.5);

      Animated.sequence([
        Animated.delay(delay),
        Animated.parallel([
          Animated.timing(translateY, {
            toValue: -150 - Math.random() * 100,
            duration: duration,
            easing: Easing.out(Easing.quad),
            useNativeDriver: true,
          }),
          Animated.timing(translateX, {
            toValue: (Math.random() - 0.5) * 100,
            duration: duration,
            easing: Easing.inOut(Easing.sin),
            useNativeDriver: true,
          }),
          Animated.sequence([
            Animated.timing(opacity, {
              toValue: 0.6,
              duration: duration * 0.2,
              useNativeDriver: true,
            }),
            Animated.timing(opacity, {
              toValue: 0,
              duration: duration * 0.8,
              useNativeDriver: true,
            }),
          ]),
          Animated.timing(scale, {
            toValue: 1.5,
            duration: duration,
            useNativeDriver: true,
          }),
        ]),
      ]).start(() => animate());
    };
    animate();
  }, []);

  return (
    <Animated.View
      style={[
        styles.particle,
        {
          left: startX,
          top: startY,
          width: size,
          height: size,
          backgroundColor: color,
          opacity,
          transform: [{ translateY }, { translateX }, { scale }],
        },
      ]}
    />
  );
};

// Generate particles
const ParticleField = () => {
  const particles = useMemo(() => {
    const colors = [
      'rgba(255, 255, 255, 0.8)',  // White
      'rgba(255, 255, 255, 0.6)',  // White (softer)
      'rgba(255, 255, 255, 0.5)',  // White (subtle)
    ];
    
    return Array.from({ length: 20 }, (_, i) => ({
      id: i,
      delay: Math.random() * 5000,
      duration: 4000 + Math.random() * 3000,
      startX: `${10 + Math.random() * 80}%`,
      startY: `${60 + Math.random() * 40}%`,
      size: 3 + Math.random() * 5,
      color: colors[Math.floor(Math.random() * colors.length)],
    }));
  }, []);

  return (
    <View style={styles.particleContainer} pointerEvents="none">
      {particles.map((p) => (
        <Particle key={p.id} {...p} />
      ))}
    </View>
  );
};

export default function MainPage() {
  const { navigateTo } = useNavigation();
  const [ctaHovered, setCtaHovered] = useState(false);
  const [adminHovered, setAdminHovered] = useState(false);
  const entryAnim = useRef(new Animated.Value(0)).current;
  const ctaScale = useRef(new Animated.Value(1)).current;
  const adminScale = useRef(new Animated.Value(1)).current;
  const glowPulse = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    // Entry animation
    Animated.timing(entryAnim, {
      toValue: 1,
      duration: 1000,
      easing: Easing.bezier(0.22, 1, 0.36, 1),
      useNativeDriver: true,
    }).start();
    
    // Subtle glow pulse
    Animated.loop(
      Animated.sequence([
        Animated.timing(glowPulse, {
          toValue: 1,
          duration: 2000,
          easing: Easing.inOut(Easing.sin),
          useNativeDriver: false,
        }),
        Animated.timing(glowPulse, {
          toValue: 0,
          duration: 2000,
          easing: Easing.inOut(Easing.sin),
          useNativeDriver: false,
        }),
      ])
    ).start();
  }, []);

  // CTA button hover
  const handleCtaHoverIn = () => {
    setCtaHovered(true);
    Animated.spring(ctaScale, { toValue: 1.05, useNativeDriver: true, friction: 8 }).start();
  };
  const handleCtaHoverOut = () => {
    setCtaHovered(false);
    Animated.spring(ctaScale, { toValue: 1, useNativeDriver: true, friction: 8 }).start();
  };

  // Admin button hover
  const handleAdminHoverIn = () => {
    setAdminHovered(true);
    Animated.spring(adminScale, { toValue: 1.05, useNativeDriver: true, friction: 8 }).start();
  };
  const handleAdminHoverOut = () => {
    setAdminHovered(false);
    Animated.spring(adminScale, { toValue: 1, useNativeDriver: true, friction: 8 }).start();
  };

  const [email, setEmail] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const onNotify = async () => {
    if (!email || sending) return;
    try {
      setSending(true);
      await saveEmailCapture(email.trim());
      setSent(true);
      setEmail('');
    } catch (e) {
      console.warn('Failed to save email', e);
    } finally {
      setSending(false);
    }
  };

  // Animated gradient opacity
  const gradientOpacity = glowPulse.interpolate({
    inputRange: [0, 1],
    outputRange: [0.3, 0.5],
  });

  return (
    <View style={styles.container}>
      <StatusBar style="light" />
      
      {/* Background gradient */}
      <LinearGradient
        colors={['#0a0a0a', '#111', '#0a0a0a']}
        style={StyleSheet.absoluteFill}
      />
      
      {/* Animated accent gradients */}
      <Animated.View style={[styles.gradientOrb, styles.gradientOrb1, { opacity: gradientOpacity }]}>
        <LinearGradient
          colors={['rgba(139, 92, 246, 0.15)', 'transparent']}
          style={StyleSheet.absoluteFill}
          start={{ x: 0.5, y: 0 }}
          end={{ x: 0.5, y: 1 }}
        />
      </Animated.View>
      <Animated.View style={[styles.gradientOrb, styles.gradientOrb2, { opacity: gradientOpacity }]}>
        <LinearGradient
          colors={['rgba(59, 130, 246, 0.1)', 'transparent']}
          style={StyleSheet.absoluteFill}
          start={{ x: 0, y: 0.5 }}
          end={{ x: 1, y: 0.5 }}
        />
      </Animated.View>
      
      {/* Particle effects */}
      <ParticleField />
      
      {/* Main content */}
      <Animated.View
        style={[
          styles.content,
          { 
            opacity: entryAnim,
            transform: [{ 
              translateY: entryAnim.interpolate({ 
                inputRange: [0, 1], 
                outputRange: [40, 0] 
              }) 
            }]
          }
        ]}
      >
        {/* Logo */}
        <View style={styles.logoContainer}>
          <Image 
            source={require('./assets/icon.png')} 
            style={styles.logo}
            resizeMode="contain"
          />
        </View>
        
        {/* Title with gradient */}
        <View style={styles.titleContainer}>
          <Text style={styles.title}>Coming Soon</Text>
          <LinearGradient
            colors={['transparent', 'rgba(139, 92, 246, 0.3)', 'transparent']}
            start={{ x: 0, y: 0.5 }}
            end={{ x: 1, y: 0.5 }}
            style={styles.titleUnderline}
          />
        </View>
        
        <Text style={styles.subtitle}>
          We're building the future of sports betting analytics.{'\n'}
          AI-powered insights across 100+ sportsbooks.
        </Text>

        {/* Feature pills */}
        <View style={styles.features}>
          {['AI Analysis', 'Real-Time Odds', '100+ Books', 'EV Calculator'].map((feature, i) => (
            <View key={i} style={styles.featurePill}>
              <LinearGradient
                colors={['rgba(139, 92, 246, 0.2)', 'rgba(59, 130, 246, 0.1)']}
                start={{ x: 0, y: 0 }}
                end={{ x: 1, y: 1 }}
                style={StyleSheet.absoluteFill}
              />
              <Text style={styles.featureText}>{feature}</Text>
            </View>
          ))}
        </View>

        {/* Email capture */}
        <View style={styles.formSection}>
          <Text style={styles.formLabel}>Get early access</Text>
          <View style={styles.formRow}>
            <View style={styles.inputWrapper}>
              <LinearGradient
                colors={['rgba(255,255,255,0.05)', 'rgba(255,255,255,0.02)']}
                style={StyleSheet.absoluteFill}
                pointerEvents="none"
              />
              <TextInput
                placeholder="your@email.com"
                placeholderTextColor="rgba(255,255,255,0.4)"
                style={styles.input}
                keyboardType="email-address"
                autoCapitalize="none"
                value={email}
                onChangeText={setEmail}
                onSubmitEditing={onNotify}
              />
            </View>
            <Animated.View style={{ transform: [{ scale: ctaScale }] }}>
              <TouchableOpacity 
                activeOpacity={0.9} 
                onPress={onNotify} 
                disabled={sending || sent}
                {...(Platform.OS === 'web' ? { 
                  onMouseEnter: handleCtaHoverIn, 
                  onMouseLeave: handleCtaHoverOut 
                } : {})}
              >
                <LinearGradient
                  colors={sent 
                    ? ['rgba(34, 197, 94, 0.8)', 'rgba(34, 197, 94, 0.6)']
                    : ctaHovered
                      ? ['#a78bfa', '#8b5cf6']
                      : ['#8b5cf6', '#7c3aed']
                  }
                  start={{ x: 0, y: 0 }}
                  end={{ x: 1, y: 1 }}
                  style={styles.ctaButton}
                >
                  <Text style={styles.ctaText}>
                    {sent ? '✓ Added' : (sending ? '...' : 'Notify Me')}
                  </Text>
                </LinearGradient>
              </TouchableOpacity>
            </Animated.View>
          </View>
        </View>

        {/* Divider */}
        <View style={styles.divider}>
          <LinearGradient
            colors={['transparent', 'rgba(255,255,255,0.15)', 'transparent']}
            start={{ x: 0, y: 0.5 }}
            end={{ x: 1, y: 0.5 }}
            style={styles.dividerLine}
          />
          <Text style={styles.dividerText}>Alpha Testers</Text>
          <LinearGradient
            colors={['transparent', 'rgba(255,255,255,0.15)', 'transparent']}
            start={{ x: 0, y: 0.5 }}
            end={{ x: 1, y: 0.5 }}
            style={styles.dividerLine}
          />
        </View>

        {/* Admin login button */}
        <Animated.View style={{ transform: [{ scale: adminScale }] }}>
          <TouchableOpacity 
            activeOpacity={0.8} 
            onPress={() => navigateTo('Login')}
            {...(Platform.OS === 'web' ? { 
              onMouseEnter: handleAdminHoverIn, 
              onMouseLeave: handleAdminHoverOut 
            } : {})}
          >
            <LinearGradient
              colors={adminHovered 
                ? ['rgba(139, 92, 246, 0.3)', 'rgba(59, 130, 246, 0.2)']
                : ['rgba(255,255,255,0.08)', 'rgba(255,255,255,0.04)']
              }
              start={{ x: 0, y: 0 }}
              end={{ x: 1, y: 1 }}
              style={[styles.adminButton, adminHovered && styles.adminButtonHovered]}
            >
              <Text style={[styles.adminButtonText, adminHovered && styles.adminButtonTextHovered]}>
                Sign In →
              </Text>
            </LinearGradient>
          </TouchableOpacity>
        </Animated.View>
      </Animated.View>
      
      {/* Footer */}
      <Text style={styles.footer}>© 2026 Optimized • All rights reserved</Text>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#0a0a0a',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
  },
  gradientOrb: {
    position: 'absolute',
    borderRadius: 999,
  },
  gradientOrb1: {
    width: 600,
    height: 600,
    top: -200,
    left: -100,
  },
  gradientOrb2: {
    width: 500,
    height: 500,
    bottom: -150,
    right: -100,
  },
  particleContainer: {
    ...StyleSheet.absoluteFillObject,
    overflow: 'hidden',
  },
  particle: {
    position: 'absolute',
    borderRadius: 999,
  },
  content: {
    alignItems: 'center',
    paddingHorizontal: 24,
    maxWidth: 500,
    zIndex: 1,
  },
  logoContainer: {
    marginBottom: 24,
    padding: 16,
    borderRadius: 20,
    backgroundColor: 'rgba(255,255,255,0.05)',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.1)',
  },
  logo: {
    width: 64,
    height: 64,
    borderRadius: 12,
  },
  titleContainer: {
    alignItems: 'center',
    marginBottom: 16,
  },
  title: {
    color: '#fff',
    fontSize: 42,
    fontWeight: '700',
    textAlign: 'center',
    letterSpacing: -1,
    ...(Platform.OS === 'web'
      ? { fontFamily: "-apple-system, system-ui, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif" }
      : {}),
  },
  titleUnderline: {
    width: 120,
    height: 3,
    borderRadius: 2,
    marginTop: 12,
  },
  subtitle: {
    color: 'rgba(255, 255, 255, 0.6)',
    fontSize: 16,
    textAlign: 'center',
    lineHeight: 24,
    marginBottom: 28,
    ...(Platform.OS === 'web'
      ? { fontFamily: "-apple-system, system-ui, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif" }
      : {}),
  },
  features: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'center',
    gap: 10,
    marginBottom: 36,
  },
  featurePill: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 20,
    borderWidth: 1,
    borderColor: 'rgba(139, 92, 246, 0.3)',
    overflow: 'hidden',
  },
  featureText: {
    color: 'rgba(255,255,255,0.8)',
    fontSize: 13,
    fontWeight: '500',
  },
  formSection: {
    width: '100%',
    marginBottom: 32,
  },
  formLabel: {
    color: 'rgba(255,255,255,0.5)',
    fontSize: 12,
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: 1.5,
    marginBottom: 12,
    textAlign: 'center',
  },
  formRow: {
    flexDirection: 'row',
    gap: 12,
  },
  inputWrapper: {
    flex: 1,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.1)',
    overflow: 'hidden',
  },
  input: {
    height: 52,
    paddingHorizontal: 18,
    color: '#fff',
    fontSize: 15,
    ...(Platform.OS === 'web' ? { outlineStyle: 'none' } : {}),
  },
  ctaButton: {
    paddingHorizontal: 28,
    height: 52,
    borderRadius: 12,
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: 130,
  },
  ctaText: {
    color: '#fff',
    fontWeight: '700',
    fontSize: 15,
  },
  divider: {
    flexDirection: 'row',
    alignItems: 'center',
    width: '100%',
    marginBottom: 20,
    gap: 16,
  },
  dividerLine: {
    flex: 1,
    height: 1,
  },
  dividerText: {
    color: 'rgba(255,255,255,0.35)',
    fontSize: 11,
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: 1.5,
  },
  adminButton: {
    paddingHorizontal: 32,
    paddingVertical: 14,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.1)',
  },
  adminButtonHovered: {
    borderColor: 'rgba(139, 92, 246, 0.5)',
  },
  adminButtonText: {
    color: 'rgba(255,255,255,0.6)',
    fontSize: 15,
    fontWeight: '600',
  },
  adminButtonTextHovered: {
    color: '#a78bfa',
  },
  footer: {
    position: 'absolute',
    bottom: 24,
    color: 'rgba(255,255,255,0.25)',
    fontSize: 12,
  },
});
